import { lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/components/layout';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { useHomePath } from '@/hooks/use-home-path';
import { LoginPage } from '@/pages/auth/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Code-split by module: an accountant who never opens Assets should not pay to
// download it.
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));

const VouchersPage = lazy(() => import('@/pages/accounting/VouchersPage').then((m) => ({ default: m.VouchersPage })));
const VoucherEntryPage = lazy(() => import('@/pages/accounting/VoucherEntryPage').then((m) => ({ default: m.VoucherEntryPage })));
const VoucherDetailPage = lazy(() => import('@/pages/accounting/VoucherDetailPage').then((m) => ({ default: m.VoucherDetailPage })));
const LedgersPage = lazy(() => import('@/pages/accounting/LedgersPage').then((m) => ({ default: m.LedgersPage })));
const LedgerStatementPage = lazy(() => import('@/pages/accounting/LedgerStatementPage').then((m) => ({ default: m.LedgerStatementPage })));
const ChartOfAccountsPage = lazy(() => import('@/pages/accounting/ChartOfAccountsPage').then((m) => ({ default: m.ChartOfAccountsPage })));
const DayBookPage = lazy(() => import('@/pages/accounting/DayBookPage').then((m) => ({ default: m.DayBookPage })));
const TrialBalancePage = lazy(() => import('@/pages/accounting/TrialBalancePage').then((m) => ({ default: m.TrialBalancePage })));
const ProfitAndLossPage = lazy(() => import('@/pages/accounting/ProfitAndLossPage').then((m) => ({ default: m.ProfitAndLossPage })));
const BalanceSheetPage = lazy(() => import('@/pages/accounting/BalanceSheetPage').then((m) => ({ default: m.BalanceSheetPage })));
const CashFlowPage = lazy(() => import('@/pages/accounting/CashFlowPage').then((m) => ({ default: m.CashFlowPage })));

const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })));
const UnitsPage = lazy(() => import('@/pages/projects/UnitsPage').then((m) => ({ default: m.UnitsPage })));
const TowersPage = lazy(() => import('@/pages/projects/ProjectSubPages').then((m) => ({ default: m.TowersPage })));
const FloorsPage = lazy(() => import('@/pages/projects/ProjectSubPages').then((m) => ({ default: m.FloorsPage })));
const CostCentresPage = lazy(() => import('@/pages/projects/CostCentresPage').then((m) => ({ default: m.CostCentresPage })));
const ProjectCostingPage = lazy(() => import('@/pages/projects/ProjectSubPages').then((m) => ({ default: m.ProjectCostingPage })));
const ProjectWipPage = lazy(() => import('@/pages/projects/ProjectSubPages').then((m) => ({ default: m.ProjectWipPage })));

const CustomersPage = lazy(() => import('@/pages/receivables/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => import('@/pages/receivables/CustomersPage').then((m) => ({ default: m.CustomerDetailPage })));
const BookingsPage = lazy(() => import('@/pages/receivables/BookingsPage').then((m) => ({ default: m.BookingsPage })));
const BookingDetailPage = lazy(() => import('@/pages/receivables/BookingDetailPage').then((m) => ({ default: m.BookingDetailPage })));
const DemandsPage = lazy(() => import('@/pages/receivables/DemandsPage').then((m) => ({ default: m.DemandsPage })));
const ReceiptsPage = lazy(() => import('@/pages/receivables/ReceiptsPage').then((m) => ({ default: m.ReceiptsPage })));
const OutstandingPage = lazy(() => import('@/pages/receivables/OutstandingPage').then((m) => ({ default: m.OutstandingPage })));
const PaymentSchedulesPage = lazy(() => import('@/pages/receivables/PaymentSchedulesPage').then((m) => ({ default: m.PaymentSchedulesPage })));

const VendorsPage = lazy(() => import('@/pages/payables/VendorsPage').then((m) => ({ default: m.VendorsPage })));
const ContractorsPage = lazy(() => import('@/pages/payables/VendorsPage').then((m) => ({ default: m.ContractorsPage })));
const VendorDetailPage = lazy(() => import('@/pages/payables/VendorsPage').then((m) => ({ default: m.VendorDetailPage })));
const PurchaseOrdersPage = lazy(() => import('@/pages/payables/PurchaseOrdersPage').then((m) => ({ default: m.PurchaseOrdersPage })));
const WorkOrdersPage = lazy(() => import('@/pages/payables/WorkOrdersPage').then((m) => ({ default: m.WorkOrdersPage })));
const RaBillsPage = lazy(() => import('@/pages/payables/RaBillsPage').then((m) => ({ default: m.RaBillsPage })));
const BillsPage = lazy(() => import('@/pages/payables/BillsPage').then((m) => ({ default: m.BillsPage })));
const PaymentsPage = lazy(() => import('@/pages/payables/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));

const MaterialsPage = lazy(() => import('@/pages/inventory/InventoryPages').then((m) => ({ default: m.MaterialsPage })));
const StoresPage = lazy(() => import('@/pages/inventory/InventoryPages').then((m) => ({ default: m.StoresPage })));
const StockPage = lazy(() => import('@/pages/inventory/InventoryPages').then((m) => ({ default: m.StockPage })));
const MaterialIssuesPage = lazy(() => import('@/pages/inventory/InventoryPages').then((m) => ({ default: m.MaterialIssuesPage })));
const TransfersPage = lazy(() => import('@/pages/inventory/InventoryPages').then((m) => ({ default: m.TransfersPage })));

const BankAccountsPage = lazy(() => import('@/pages/banking/BankingPages').then((m) => ({ default: m.BankAccountsPage })));
const BankTransactionsPage = lazy(() => import('@/pages/banking/BankingPages').then((m) => ({ default: m.BankTransactionsPage })));
const ReconciliationPage = lazy(() => import('@/pages/banking/BankingPages').then((m) => ({ default: m.ReconciliationPage })));

const GstPage = lazy(() => import('@/pages/taxes/TaxPages').then((m) => ({ default: m.GstPage })));
const TdsPage = lazy(() => import('@/pages/taxes/TaxPages').then((m) => ({ default: m.TdsPage })));
const TaxReportsPage = lazy(() => import('@/pages/taxes/TaxPages').then((m) => ({ default: m.TaxReportsPage })));

const LoansPage = lazy(() => import('@/pages/finance/FinancePages').then((m) => ({ default: m.LoansPage })));
const EmiSchedulePage = lazy(() => import('@/pages/finance/FinancePages').then((m) => ({ default: m.EmiSchedulePage })));
const InterestPage = lazy(() => import('@/pages/finance/FinancePages').then((m) => ({ default: m.InterestPage })));

const AssetsPage = lazy(() => import('@/pages/assets/AssetPages').then((m) => ({ default: m.AssetsPage })));
const DepreciationPage = lazy(() => import('@/pages/assets/AssetPages').then((m) => ({ default: m.DepreciationPage })));
const AssetRegisterPage = lazy(() => import('@/pages/assets/AssetPages').then((m) => ({ default: m.AssetRegisterPage })));

const LandownersPage = lazy(() => import('@/pages/landowners/LandownersPage').then((m) => ({ default: m.LandownersPage })));
const ReportsHubPage = lazy(() => import('@/pages/reports/ReportsHubPage').then((m) => ({ default: m.ReportsHubPage })));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const ApprovalsPage = lazy(() => import('@/pages/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SimpleHomePage = lazy(() => import('@/pages/simple/SimpleHomePage').then((m) => ({ default: m.SimpleHomePage })));
const SimpleOverviewPage = lazy(() => import('@/pages/simple/SimpleOverviewPage').then((m) => ({ default: m.SimpleOverviewPage })));
const SimpleOwedToUsPage = lazy(() => import('@/pages/simple/SimpleOwedPages').then((m) => ({ default: m.SimpleOwedToUsPage })));
const SimpleWeOwePage = lazy(() => import('@/pages/simple/SimpleOwedPages').then((m) => ({ default: m.SimpleWeOwePage })));
const SimpleLandownersPage = lazy(() => import('@/pages/simple/SimpleOwedPages').then((m) => ({ default: m.SimpleLandownersPage })));
const SimpleSpendingPage = lazy(() => import('@/pages/simple/SimpleSpendingPage').then((m) => ({ default: m.SimpleSpendingPage })));
const SimpleMoneyInPage = lazy(() => import('@/pages/simple/SimpleMoneyPages').then((m) => ({ default: m.SimpleMoneyInPage })));
const SimpleMoneyOutPage = lazy(() => import('@/pages/simple/SimpleMoneyPages').then((m) => ({ default: m.SimpleMoneyOutPage })));
const SimplePartyPage = lazy(() => import('@/pages/simple/SimplePartyPage').then((m) => ({ default: m.SimplePartyPage })));
const IntegrationsPage = lazy(() => import('@/pages/integrations/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })));

/**
 * Where signing in lands you.
 *
 * Everyone used to arrive at the accountant's dashboard — a store manager
 * included, who then had to find their way to Simple Mode themselves. The
 * final product principle is explicit that the builder and the site manager get
 * a simple business interface, and the front door was the one place still
 * handing them a ledger screen.
 *
 * Roles are loaded before this renders: `RequireAuth` holds the whole subtree
 * until bootstrap finishes, so the mode here is the person's real mode and not
 * the empty-role default.
 */
function Landing() {
  return <Navigate to={useHomePath()} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Landing /> },

      /*
        Simple Mode. It sits beside the existing screens rather than replacing
        them: the same data, asked for in business language.

        The overview is the landing screen, not the recorder — a director can
        see the business but is deliberately not a person who types
        transactions, and landing them on "what do you want to record?" would be
        a dead end for the role the mode is most for.
      */
      { path: 'simple', element: <SimpleOverviewPage /> },
      { path: 'simple/record', element: <SimpleHomePage /> },
      { path: 'simple/owed-to-us', element: <SimpleOwedToUsPage /> },
      { path: 'simple/we-owe', element: <SimpleWeOwePage /> },
      { path: 'simple/spending', element: <SimpleSpendingPage /> },
      { path: 'simple/landowners', element: <SimpleLandownersPage /> },
      { path: 'simple/money-in', element: <SimpleMoneyInPage /> },
      { path: 'simple/money-out', element: <SimpleMoneyOutPage /> },
      { path: 'simple/party/:partyId', element: <SimplePartyPage /> },
      { path: 'dashboard', element: <DashboardPage /> },

      // Accounting
      { path: 'accounting/vouchers', element: <VouchersPage /> },
      { path: 'accounting/vouchers/new', element: <VoucherEntryPage /> },
      { path: 'accounting/vouchers/:id/edit', element: <VoucherEntryPage /> },
      { path: 'accounting/vouchers/:id', element: <VoucherDetailPage /> },
      { path: 'accounting/ledgers', element: <LedgersPage /> },
      { path: 'accounting/ledgers/:id', element: <LedgerStatementPage /> },
      { path: 'accounting/chart-of-accounts', element: <ChartOfAccountsPage /> },
      { path: 'accounting/day-book', element: <DayBookPage /> },
      { path: 'accounting/trial-balance', element: <TrialBalancePage /> },
      { path: 'accounting/profit-and-loss', element: <ProfitAndLossPage /> },
      { path: 'accounting/balance-sheet', element: <BalanceSheetPage /> },
      { path: 'accounting/cash-flow', element: <CashFlowPage /> },

      // Projects — specific paths before the :id catch-all.
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'projects/towers', element: <TowersPage /> },
      { path: 'projects/floors', element: <FloorsPage /> },
      { path: 'projects/cost-centres', element: <CostCentresPage /> },
      { path: 'projects/units', element: <UnitsPage /> },
      { path: 'projects/units/:id', element: <UnitsPage /> },
      { path: 'projects/costing', element: <ProjectCostingPage /> },
      { path: 'projects/wip', element: <ProjectWipPage /> },
      { path: 'projects/:id', element: <ProjectDetailPage /> },
      { path: 'projects/:id/costing', element: <ProjectDetailPage /> },

      // Receivables
      { path: 'receivables/customers', element: <CustomersPage /> },
      { path: 'receivables/customers/:id', element: <CustomerDetailPage /> },
      { path: 'receivables/bookings', element: <BookingsPage /> },
      { path: 'receivables/bookings/:id', element: <BookingDetailPage /> },
      { path: 'receivables/bookings/:id/statement', element: <BookingDetailPage /> },
      { path: 'receivables/payment-schedules', element: <PaymentSchedulesPage /> },
      { path: 'receivables/demands', element: <DemandsPage /> },
      { path: 'receivables/receipts', element: <ReceiptsPage /> },
      { path: 'receivables/outstanding', element: <OutstandingPage /> },

      // Payables
      { path: 'payables/vendors', element: <VendorsPage /> },
      { path: 'payables/vendors/:id', element: <VendorDetailPage /> },
      { path: 'payables/contractors', element: <ContractorsPage /> },
      { path: 'payables/purchase-orders', element: <PurchaseOrdersPage /> },
      { path: 'payables/work-orders', element: <WorkOrdersPage /> },
      { path: 'payables/ra-bills', element: <RaBillsPage /> },
      { path: 'payables/bills', element: <BillsPage /> },
      { path: 'payables/payments', element: <PaymentsPage /> },
      { path: 'payables/outstanding', element: <VendorsPage /> },

      // Inventory
      { path: 'inventory/materials', element: <MaterialsPage /> },
      { path: 'inventory/stores', element: <StoresPage /> },
      { path: 'inventory/stock', element: <StockPage /> },
      { path: 'inventory/issues', element: <MaterialIssuesPage /> },
      { path: 'inventory/transfers', element: <TransfersPage /> },

      // Banking
      { path: 'banking/accounts', element: <BankAccountsPage /> },
      { path: 'banking/transactions', element: <BankTransactionsPage /> },
      { path: 'banking/reconciliation', element: <ReconciliationPage /> },

      // Taxes
      { path: 'taxes/gst', element: <GstPage /> },
      { path: 'taxes/tds', element: <TdsPage /> },
      { path: 'taxes/reports', element: <TaxReportsPage /> },

      // Finance
      { path: 'finance/loans', element: <LoansPage /> },
      { path: 'finance/emi', element: <EmiSchedulePage /> },
      { path: 'finance/interest', element: <InterestPage /> },

      // Assets
      { path: 'assets', element: <AssetsPage /> },
      { path: 'assets/depreciation', element: <DepreciationPage /> },
      { path: 'assets/register', element: <AssetRegisterPage /> },

      // Everything else
      { path: 'landowners', element: <LandownersPage /> },
      { path: 'reports', element: <ReportsHubPage /> },
      { path: 'documents', element: <DocumentsPage /> },
      { path: 'approvals', element: <ApprovalsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'integrations', element: <IntegrationsPage /> },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
